import { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getReviews } from '../lib/api';

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await getReviews();
        setReviews(res.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="reviews-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <p className="text-xs sm:text-sm font-semibold tracking-widest uppercase text-neutral-500 mb-3 sm:mb-4">Testimonials</p>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-tight text-neutral-900 mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Reviews</h1>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500 max-w-2xl mx-auto px-4">
            See what our clients have to say about their experience with us.
          </p>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-12 sm:py-16 md:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          {reviews.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500">No reviews available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {reviews.map((review) => (
                <div 
                  key={review.id} 
                  className="bg-white border border-neutral-100 p-5 sm:p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300"
                  data-testid={`review-${review.id}`}
                >
                  <div className="flex gap-1 mb-3 sm:mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-neutral-200'}
                      />
                    ))}
                  </div>
                  
                  <p className="text-sm sm:text-base leading-relaxed text-neutral-500 mb-4 sm:mb-6">
                    "{review.content}"
                  </p>
                  
                  <div className="border-t border-neutral-100 pt-3 sm:pt-4">
                    <p className="font-semibold text-neutral-900 text-sm sm:text-base">{review.name}</p>
                    <p className="text-xs sm:text-sm text-neutral-500">{review.event_type}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 text-center">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>Ready to Create Your Story?</h2>
          <p className="text-base sm:text-lg md:text-xl leading-relaxed text-neutral-500 max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
            Join our growing list of happy clients and make your event unforgettable.
          </p>
          <a href="/packages" className="bg-neutral-900 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg shadow-neutral-900/20 inline-block text-sm sm:text-base">
            View Packages
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReviewsPage;
